import { useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { Chart } from '@vnetwork/ui/charts';
import { useToast } from '@vnetwork/ui/feedback';
import { EmptyState } from '@vnetwork/ui/data';
import { Button, Skeleton, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@vnetwork/ui/primitives';
import { LayoutDashboard } from 'lucide-react';
import type { LmsTranscodingDurationRange } from '@/api/media/live-analysis-types';
import {
  useLmsRecordingDurationUsage,
  useLmsTranscodingDurationUsage,
  fetchLmsAnalysisCsv,
} from '@/api/media/live-analysis';
import {
  ANALYSIS_SERIES_GROUPS,
  buildRecordingData,
  buildTranscodingData,
} from '../analysis/analysis-utils';
import { ChartCard, hasSeriesData } from '../../_shared/AnalyticsKit';
import styles from './DashboardPage.module.css';

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

interface OverviewChartsProps {
  serviceId: string;
}

/**
 * Overview recording + transcoding duration charts for the Dashboard page.
 * Uses the "overview" series group (total / audio-only / transmux).
 */
export function OverviewCharts({ serviceId }: OverviewChartsProps) {
  const toast = useToast();
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonthIndex = now.getMonth();
  const yearOptions = [currentYear, currentYear - 1];

  const [year, setYear] = useState(currentYear);
  const [transcodingRange, setTranscodingRange] = useState<LmsTranscodingDurationRange>('30');
  const [downloadingType, setDownloadingType] = useState<'recording-duration' | 'transcoding-duration' | null>(null);

  const { data: recordingUsage = [], isPending: recordingLoading, isError: recordingError } =
    useLmsRecordingDurationUsage(serviceId, year);

  const { data: transcodingUsage = [], isPending: transcodingLoading, isError: transcodingError } =
    useLmsTranscodingDurationUsage(serviceId, transcodingRange);

  const group = ANALYSIS_SERIES_GROUPS['overview'];

  const recordingData = useMemo(
    () => buildRecordingData(recordingUsage, year, currentYear, currentMonthIndex, group.recordingKeys),
    [recordingUsage, year, currentYear, currentMonthIndex, group.recordingKeys],
  );

  const transcodingData = useMemo(
    () => buildTranscodingData(transcodingUsage, group.transcodingKeys),
    [transcodingUsage, group.transcodingKeys],
  );

  const handleDownload = async (type: 'recording-duration' | 'transcoding-duration') => {
    setDownloadingType(type);
    try {
      // Transcoding CSV endpoint only supports year-based export; use current year.
      const csvYear = type === 'transcoding-duration' ? currentYear : year;
      const blob = await fetchLmsAnalysisCsv(serviceId, type, csvYear);
      triggerDownload(blob, `lms-${type}-${csvYear}.csv`);
    } catch (err) {
      toast.error((err as Error).message || `Failed to download ${type} CSV`);
    } finally {
      setDownloadingType(null);
    }
  };

  return (
    <div className={styles.chartsRow}>
      {/* Recording duration chart */}
      <ChartCard
        title="Overview — Recording Duration"
        description={group.subtitle}
        error={recordingError}
        actions={
          <div className={styles.chartActions}>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger size="sm" style={{ width: 90 }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="secondary"
              size="sm"
              startIcon={<Download size={13} />}
              disabled={downloadingType === 'recording-duration'}
              onClick={() => void handleDownload('recording-duration')}
            >
              {downloadingType === 'recording-duration' ? 'Downloading…' : 'CSV'}
            </Button>
          </div>
        }
      >
        {recordingLoading ? (
          <Skeleton variant="rect" height={200} />
        ) : !hasSeriesData(recordingData.rows, 'label') ? (
          <EmptyState icon={<LayoutDashboard size={20} />} title="No recording data" description="No recording data available for the selected period." size="sm" />
        ) : (
          <Chart
            type="line"
            data={recordingData.rows}
            xKey="label"
            series={recordingData.series}
            height={200}
            showLegend={recordingData.series.length > 1}
            valueFormatter={(v, key) => (key ? `${Number(v).toLocaleString()} Hour(s)` : Number(v).toLocaleString())}
          />
        )}
      </ChartCard>

      {/* Transcoding duration chart */}
      <ChartCard
        title="Overview — Transcoding Duration"
        description={group.subtitle}
        error={transcodingError}
        actions={
          <div className={styles.chartActions}>
            <Select
              value={transcodingRange}
              onValueChange={(v) => setTranscodingRange(v as LmsTranscodingDurationRange)}
            >
              <SelectTrigger size="sm" style={{ width: 120 }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="secondary"
              size="sm"
              startIcon={<Download size={13} />}
              disabled={downloadingType === 'transcoding-duration'}
              onClick={() => void handleDownload('transcoding-duration')}
            >
              {downloadingType === 'transcoding-duration' ? 'Downloading…' : 'CSV'}
            </Button>
          </div>
        }
      >
        {transcodingLoading ? (
          <Skeleton variant="rect" height={200} />
        ) : !hasSeriesData(transcodingData.rows, 'label') ? (
          <EmptyState icon={<LayoutDashboard size={20} />} title="No transcoding data" description="No transcoding data available for the selected period." size="sm" />
        ) : (
          <Chart
            type="line"
            data={transcodingData.rows}
            xKey="label"
            series={transcodingData.series}
            height={200}
            showLegend={transcodingData.series.length > 1}
            valueFormatter={(v, key) => (key ? `${Number(v).toLocaleString()} Hour(s)` : Number(v).toLocaleString())}
          />
        )}
      </ChartCard>
    </div>
  );
}
